import { KNOP_IN_KOP } from "@/components/rust/Rust";
import React from "react";
import { Link } from "react-router-dom";
import { Teller, SectieBalk, AfspraakLijst, InternBlok, Leeg, ReactieRegel } from "./VandaagParts";

const STAND = {
  klaar: ["Klaar 4/4", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  bezig: [null, "bg-blue-50 text-blue-700 border-blue-200"],
  leeg: ["Leeg 0/4", "bg-amber-100 text-amber-800 border-amber-200"],
  "te-doen": ["Te doen", "bg-zinc-100 text-zinc-500 border-zinc-200"],
  // v21-R1 R3: vanaf de nieuwe regels de staat uit call_staat
  "klaar-te-laat": ["Klaar, te laat", "bg-amber-50 text-amber-800 border-amber-200"],
  "niet-klaar": ["Gebeld, nog niet klaar", "bg-blue-50 text-blue-700 border-blue-200"],
  "niet-gebeld": ["Nog niet gebeld", "bg-zinc-100 text-zinc-500 border-zinc-200"],
  "te-laat": ["Te laat", "bg-rose-50 text-rose-700 border-rose-200"],
};
const NIEUW_KLAAR = new Set(["klaar", "klaar-te-laat"]);
const chip = "text-[10px] font-bold uppercase tracking-wider rounded-full border px-2 py-0.5";

const Aangeleverd = ({ a, loc }) => {
  if (!a || a.status === "geen-account") return null;
  // R1: 'Niets aangeleverd' is pas een feit na sluiting van het venster; daarvóór amber 'Nog aan te leveren' (zoals de kaartchip).
  const tekst = a.status === "open" ? "Nog aan te leveren" : a.status === "nog-niet-open" ? "Venster nog niet open" : a.status === "niets" ? "Niets aangeleverd" : a.totaal > 0 ? `Aangeleverd ${a.ingevuld}/${a.totaal}` : "Aangeleverd";
  const cls = a.status === "volledig" ? "bg-zinc-100 text-zinc-500 border-zinc-200" : a.status === "niets" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-800 border-amber-200";
  return <span data-testid={`vandaag-aangeleverd-${loc}`} className={`${chip} ${cls}`}>{tekst}</span>;
};

function CallRij({ x, week, me }) {
  const [txt0, cls] = STAND[x.stand];
  const txt = x.nieuw && x.stand === "klaar" ? "Klaar" : txt0;
  return (
    <Link to={`/locatie-call?week=${week}&om=${me}&locatie=${encodeURIComponent(x.locatie)}`} data-testid={`vandaag-call-${x.locatie}`}
      className="flex flex-wrap items-center gap-2 px-3 py-2.5 hover:bg-zinc-50 transition-colors">
      <span className="font-heading font-black text-zinc-900 min-w-[120px]">{x.locatie}</span>
      <span data-testid={`vandaag-call-stand-${x.locatie}`} className={`${chip} ${cls}`}>{txt || `Bezig ${x.cijfers}/4`}</span>
      {x.reden && <span data-testid={`vandaag-call-reden-${x.locatie}`} className="text-xs text-zinc-500">{x.reden}</span>}
      <Aangeleverd a={x.aangeleverd} loc={x.locatie} />
      {x.vorige?.rood && <span className={`${chip} bg-white text-rose-700 border-rose-200`}>Week {x.vorige.overWeekNr} rood · {x.vorige.rodeKpis.join(", ")}</span>}
      {x.terugkerend && <span className={`${chip} bg-rose-50 text-rose-700 border-rose-300`}>Terugkerend · {x.roodAantal} van 13</span>}
      {x.teLaat > 0 && <span className="font-mono text-xs text-rose-600 font-bold">{x.teLaat} {x.teLaat === 1 ? "afspraak" : "afspraken"} te laat</span>}
    </Link>
  );
}

function VoorbereidingRij({ x }) {
  const v = x.voorbereidingVolgende;
  const tekst = v.status === "geen-account" ? "geen bedrijfsleider-account" : v.status === "nog-niet-open" ? `opent ${v.opentLabel}` : v.status === "open" ? `nog niets · open tot ${v.sluitLabel}` : v.status === "niets" ? "niets aangeleverd" : v.totaal > 0 ? `${v.ingevuld}/${v.totaal}` : "aangeleverd";
  return (
    <div data-testid={`vandaag-vb-${x.locatie}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5">
      <span className="font-heading font-black text-zinc-900 min-w-[120px]">{x.locatie}</span>
      <span className={`font-mono text-sm ${v.status === "volledig" ? "text-emerald-700" : v.status === "onvolledig" ? "text-amber-800" : "text-zinc-500"}`}>{tekst}</span>
      {x.weekritmeBl && <span className="font-mono text-xs text-zinc-500">weekritme {x.weekritmeBl.gedaan}/{x.weekritmeBl.totaal}</span>}
    </div>
  );
}

// R16: wat er echt gebeld is, uit de rijen — met een link als er locaties ontbreken.
function GebeldHint({ d, me }) {
  const cw = d.callWeek;
  const gebeld = d.locaties.filter((x) => x.gebeld).length;
  const compleet = d.locaties.filter((x) => NIEUW_KLAAR.has(x.stand)).length;
  return (
    <span data-testid="vandaag-gebeld-hint">
      call {cw.callLabel} · over week {cw.overWeekNr}: {gebeld} van {d.locaties.length} gebeld · {compleet} klaar
      {gebeld < d.locaties.length && <> · <Link to={`/locatie-call?week=${cw.week}&om=${me}`} data-testid="vandaag-naar-locatiecall" className="font-semibold text-blue-300 hover:text-white underline underline-offset-2">naar Locatie-call</Link></>}
    </span>
  );
}

const tijd = (iso) => new Date(iso).toLocaleString("nl-NL", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function NieuwLijst({ items }) {
  if (!items.length) return <Leeg>Niets nieuws.</Leeg>;
  return (
    <ul data-testid="vandaag-nieuw" className="bg-white border border-zinc-200 rounded-[10px] divide-y divide-zinc-200">
      {items.map((n, i) => (
        <li key={i} data-testid={`vandaag-nieuw-${i}`} className="px-3 py-[9px] text-[13px] flex flex-wrap gap-x-3 gap-y-0.5 items-baseline">
          {n.soort === "reactie" ? <ReactieRegel n={n} testid={`vandaag-nieuw-reactie-${i}`} /> : <span className="font-semibold text-zinc-900">{n.locatie || "Algemeen"}</span>}
          {n.soort === "reactie" ? null : n.soort === "afdeling-voortgang" ? (
            <Link to={`/afspraken?select=${n.afspraakId}`} data-testid={`vandaag-nieuw-afdeling-${i}`} className="flex-1 min-w-[160px] text-zinc-700 hover:text-blue-700">
              Afdeling meldt ({n.afdelingNaam}){n.bij === "deadline" ? " bij de deadline" : ""}{n.gereed ? " · gereed" : ""} · {n.wat}{n.note && <span className="text-zinc-500"> — {n.note}</span>}
            </Link>
          ) : n.soort === "punt" ? (
            <Link to={`/afspraken?select=${n.afspraakId}`} data-testid={`vandaag-nieuw-punt-${i}`} className="flex-1 min-w-[160px] text-zinc-700 hover:text-blue-700">
              voegde een punt toe: {n.wat}
            </Link>
          ) : n.soort === "voortgang" ? (
            <Link to={`/afspraken?select=${n.afspraakId}`} className="flex-1 min-w-[160px] text-zinc-700 hover:text-blue-700">
              {n.bij === "deadline" ? `Antwoord bij de deadline${n.gereed ? " · gereed" : ""}` : n.gereed ? "Deelpunt gereed gemeld" : "Voortgang gemeld"} · {n.wat}{n.note && <span className="text-zinc-500"> — {n.note}</span>}
            </Link>
          ) : <span className="flex-1 min-w-[160px] text-zinc-700">Voorbereiding voor {n.callLabel} aangeleverd of bijgewerkt</span>}
          {n.soort !== "reactie" && <span className="font-mono text-xs text-zinc-400">{n.at ? tijd(n.at) : ""}{n.door ? ` · ${n.door}` : ""}</span>}
        </li>
      ))}
    </ul>
  );
}

export default function OmVandaag({ d, me, onGezien }) {
  const t = d.tellers;
  const cw = d.callWeek;
  const vc = d.volgendeCall;
  // R5: zonder toewijzing vervangt de melding alleen de locatieblokken; de rest blijft staan.
  const geen = d.geenToewijzing && (
    <div data-testid="vandaag-geen-toewijzing" className="mb-3 md:mb-4 bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-sm text-amber-900">
      <p className="font-semibold">Er zijn nog geen locaties aan je toegewezen.</p>
      <p>Vraag Johannes of Milos om je locaties toe te wijzen in Instellingen → Locaties & OM-toewijzing.</p>
    </div>
  );
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-3 md:mb-4" data-testid="vandaag-tellers">
        {d.isCallDag ? <Teller testid="teller-calls" label="Calls vandaag" value={`${t.callsKlaar}/${t.locaties}`} />
          : <Teller testid="teller-voorbereiding" label={`Voorbereiding voor ${vc.callLabel}`} value={`${t.voorbereidingBegonnen} van ${t.voorbereidingTotaal}`} sub="begonnen" />}
        <Teller testid="teller-te-laat" label="Te laat" value={t.teLaat} rood={t.teLaat > 0} />
        <Teller testid="teller-te-beoordelen" label="Te beoordelen" value={t.teBeoordelen ?? 0} />
        <Teller testid="teller-deze-week" label="Deze week" value={t.dezeWeek} />
        <Teller testid="teller-nieuw" label="Nieuw" value={t.nieuw} />
      </div>
      {geen || (d.isCallDag ? (
        <div className="mb-3 md:mb-4">
          <SectieBalk testid="vandaag-sectie-calls" titel={`Calls van vandaag · over week ${cw.overWeekNr}`} aantal={d.locaties.length} />
          <div className="bg-white border border-zinc-200 rounded-[10px] divide-y divide-zinc-200">{d.locaties.map((x) => <CallRij key={x.locatie} x={x} week={cw.week} me={me} />)}</div>
        </div>
      ) : (
        <div className="mb-3 md:mb-4">
          <SectieBalk testid="vandaag-sectie-voorbereiding" titel={`Voorbereiding voor ${vc.callLabel}`} aantal={d.locaties.length} hint={<GebeldHint d={d} me={me} />} />
          <div className="bg-white border border-zinc-200 rounded-[10px] divide-y divide-zinc-200">{d.locaties.map((x) => <VoorbereidingRij key={x.locatie} x={x} />)}</div>
        </div>
      ))}
      <div className="mb-3 md:mb-4">
        <SectieBalk testid="vandaag-sectie-te-beoordelen" titel="Te beoordelen" aantal={(d.teBeoordelen || []).length} hint="de locatie of afdeling meldde; jij vinkt af" />
        <AfspraakLijst items={d.teBeoordelen || []} testid="vandaag-te-beoordelen" leeg="Niets te beoordelen." />
        <SectieBalk testid="vandaag-sectie-wachten" titel="Afspraken die op jou wachten" aantal={d.wachten.length} hint="eerst te laat, dan deze week" />
        <AfspraakLijst items={d.wachten} testid="vandaag-wachten" leeg="Niets te laat en niets deze week." />
      </div>
      <div className="mb-3 md:mb-4">
        <SectieBalk testid="vandaag-sectie-nieuw" titel="Nieuw sinds je vorige bezoek" aantal={d.nieuw.length} hint="tot je op Gezien drukt, maximaal 7 dagen"
          actie={d.nieuw.length > 0 && <button type="button" data-testid="vandaag-nieuw-gezien" onClick={onGezien} className={KNOP_IN_KOP}>Gezien</button>} />
        <NieuwLijst items={d.nieuw} />
      </div>
      <div className="mb-3 md:mb-4">
        <SectieBalk testid="vandaag-sectie-weekritme" titel={`Weekritme vandaag · ${d.weekritmeVandaag.dag}`} aantal={`${d.weekritmeVandaag.gedaan}/${d.weekritmeVandaag.totaal}`} />
        <div className="bg-white border border-zinc-200 rounded-[10px] px-3 py-2.5 space-y-1.5" data-testid="vandaag-weekritme">
          {d.weekritmeVandaag.taken.length === 0 ? <p className="text-sm text-zinc-500">Geen taken voor vandaag.</p> : d.weekritmeVandaag.taken.map((k) => (
            <p key={k.id} className={`text-sm ${k.done ? "text-zinc-400 line-through" : "text-zinc-800"}`}>{k.done ? "✓ " : "○ "}{k.text}</p>
          ))}
          <p className="text-sm text-zinc-500 pt-1">{d.weekritmeVandaag.gedaan} van {d.weekritmeVandaag.totaal} gedaan · <Link to="/weekritme" data-testid="vandaag-naar-weekritme" className="font-semibold text-blue-700 hover:text-blue-900">Naar Weekritme</Link></p>
        </div>
      </div>
      <InternBlok items={d.internMetJou} />
    </>
  );
}
